import React, { useState } from 'react';
import useStyles from './RatingChecklist.styles';
import clsx from 'clsx';
import { get, size } from 'global/lib/lodash';
import { getColorFromValue } from 'global/styles';
import { useLocation } from 'react-router-dom';
import { getUrlParams } from 'global/utils';
import { useScreen } from 'global/styles';
import { Column } from 'global/components/row';
import { Button } from 'global/components/Button';
import { Box, Icon } from '@mui/material';
import { getDisplayScoreLabel } from 'global/utils/getDisplayScore';
import { useEdit } from 'global/hooks/useEdit';
import CheckpointInfoModal from 'global/components/CheckpointInfo';
import { useShowChanges } from 'global/hooks/useShowChanges';
import DiffViewer from 'global/components/diff-text';

const percentify = (cols) => {
  return `${(1 / size(cols)) * 100}%`;
};

const RatingRow = (props) => {
  const { cols } = props;
  const classes = useStyles();

  return (
    <div className={classes.ratingRow}>
      <div className={clsx(classes.cpName, classes.textCol)} />
      {cols.map((c, i) => (
        <div className={classes.col} style={{ backgroundColor: c.color, width: percentify(cols) }}>
          {c.title}
        </div>
      ))}
    </div>
  );
};

export default (props) => {
  const loca = useLocation();
  const { checklist, parentChecklist, editMode, optionVals, edition, needsUpdate, setNeedsUpdate, userCanEdit } = props;
  const checkpoints = get(checklist, 'checkpoints', []);
  const { isDown } = useScreen('sm');
  const editionConfig = get(edition, 'config', {});
  const [checkpointInfo, setCheckpointInfo] = useState({});
  const { editOn } = useEdit();
  const { showChangesOn } = useShowChanges();

  const showChanges = get(props, 'showChanges');

  const canShowIncidentalData = !(checkpoints.length === 1);

  const cols = optionVals.sort((a, b) => a - b).map((v, i) => ({
    title: getDisplayScoreLabel({ value: v }, i, editionConfig, true),
    value: v,
    color: getColorFromValue(v),
  }));

  const classes = useStyles({ isMobile: isDown });

  const params = getUrlParams(loca.search);

  const sortCheckpoints = (a, b) => {
    return a.name.localeCompare(b.name);
  };

  const handleSetCheckpointInfo = (cp, checklist) => {
    setCheckpointInfo({
      open: true,
      checkpoint: { ...cp },
      checklist: checklist,
      edition: edition,
      optionVals: optionVals,
      onClose: () => {
        setCheckpointInfo({ open: false });
        setNeedsUpdate((prev) => {
          if (prev) {
            props.update && props.update();
          }
          return false;
        });
      },
    });
  };

  return (
    <>
      {!isDown && <RatingRow cols={cols} />}

      {(checkpoints || []).sort(sortCheckpoints).map((cp, i) => {
        const bg = i % 2;
        const highlight = get(params, 'hl') === cp.id;
        // incidental data includes images, notes and comments, because these don't display
        // translations aren't included, since they can toggle to display.
        const hasIncidentalData = cp.notes || size(get(cp, 'images')) > 0 || (userCanEdit && size(get(cp, 'comments', [])) > 0);
        const hasCheckpointName = cp.checkpointOptions?.some((cpo) => cpo.name != null);
        const parentCheckpoint = showChanges ? get(parentChecklist, 'checkpoints', []).find((pcp) => pcp.id === cp.id || pcp.id === cp.checkpointId) || {} : undefined;

        return (
          <Column key={i} className={classes[`cp_${bg}`]} style={{ alignItems: 'flex-start' }}>
            <div className={clsx(classes.cpRow, highlight && classes.hlRow)}>
              <div className={clsx(classes.cpName, classes.textCol)}>
                {parentCheckpoint !== undefined ? (
                  <>
                    <DiffViewer oldText={get(parentCheckpoint, 'name', '')} newText={cp.name} />
                    {cp.description && (
                      <div style={{ fontWeight: 'initial' }}>
                        <DiffViewer oldText={get(parentCheckpoint, 'description', '')} newText={cp.description} />
                      </div>
                    )}{' '}
                  </>
                ) : (
                  <>
                    <span>{cp.name}</span>
                    {cp.description && <div style={{ fontWeight: 'initial' }}>{cp.description}</div>}
                  </>
                )}
              </div>

              <div className={classes.ratingWrap}>
                {cols.map((c, _i) => {
                  const hasVal = get(cp, 'checkpointOptions', []).find((cpo) => cpo.value === c.value);
                  const hasParentVal = get(parentCheckpoint, 'checkpointOptions', []).find((cpo) => cpo.value === c.value);

                  if (showChanges) {
                    // Show diff between parent and current checkpoint option name
                    const oldName = get(hasParentVal, 'name', '');
                    const newName = get(hasVal, 'name', '');
                    return isDown ? (
                      <div key={_i} className={clsx(classes.ratingRow, classes.textSmall)}>
                        <div className={clsx(classes.colMobile, hasCheckpointName && classes.colMobileMaxWidth)}
                             style={{ backgroundColor: c.color }}>
                          {c.title}
                        </div>
                        {hasCheckpointName && (
                          <div className={classes.textCol}>
                            <DiffViewer oldText={oldName} newText={newName} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div key={_i} className={clsx(classes.col, classes.textCol, classes.textSmall)}
                           style={{ width: percentify(cols) }}>
                        <DiffViewer oldText={oldName} newText={newName} />
                      </div>
                    );
                  } else {
                    return isDown ? (
                      <div key={_i} className={clsx(classes.ratingRow, classes.textSmall)}>
                        <div className={clsx(classes.colMobile, hasCheckpointName && classes.colMobileMaxWidth)}
                             style={{ backgroundColor: c.color }}>
                          {c.title}
                        </div>
                        {hasCheckpointName && <div className={classes.textCol}>{get(hasVal, 'name', '')}</div>}
                      </div>
                    ) : (
                      <div key={_i} className={clsx(classes.col, classes.textCol, classes.textSmall)}
                           style={{ width: percentify(cols) }}>
                        {get(hasVal, 'name', '')}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
            <div style={{ width: isDown ? '100%' : '50%' }}>
              {!props.printView && (hasIncidentalData || editOn) && canShowIncidentalData && (
                <>
                  <Button displayPrint="none" variant="text" className={classes.row}
                          onClick={() => handleSetCheckpointInfo(cp, checklist)}>
                    {hasIncidentalData ? (
                      <>
                        {size(get(cp, 'images')) > 0 && (
                          <Icon color={'secondary'} fontSize="small">
                            photo_library
                          </Icon>
                        )}
                        {get(cp, 'notes') && (
                          <Icon color={'secondary'} fontSize="small">
                            text_snippet
                          </Icon>
                        )}
                        {userCanEdit && size(get(cp, 'comments')) > 0 && (
                          <Icon color={'secondary'} fontSize="small">
                            message
                          </Icon>
                        )}
                      </>
                    ) : (
                      <Icon color={'secondary'} fontSize="small">
                        info
                      </Icon>
                    )}
                  </Button>
                </>
              )}
              {/* {!editMode && !!size(images) && !props.printView && (
                <Box displayPrint="none" className={classes.row}>
                  <ImageList displayAsLink images={cp.images} />
                </Box>
              )} */}
              {!!editMode && props.renderForm(cp, i)}
            </div>
          </Column>
        );
      })}
      <CheckpointInfoModal {...checkpointInfo} needsUpdate={needsUpdate} setNeedsUpdate={setNeedsUpdate} />
    </>
  );
};
